import React, { useCallback, useMemo } from "react";
import { Tabs, Tab } from "@bloomberg/media-ui-core";
import { trackClickEvent } from "@foundation/nextjs-gtm";
import styles from "./quotePagePanel.module.css";
import SummaryContainer from "~components/SummaryContainer";
import RelatedNewsContainer from "~components/RelatedNewsContainer";
import CompanyInfoContainer from "~components/CompanyInfoContainer";
import FinancialsContainer from "~components/FinancialsContainer";
import { useTranslateMessage } from "~utils/i18n";
import { LabelIdKey } from "~utils/i18n/types";
import { useLocale } from "~hooks/i18n/useLocale";
import { usePageData } from "~context/PageContext";
import QuoteMembers from "~components/QuoteMembers";
import type { QuotePageData } from "~types/types";

type QuotePagePanelProps = {
  mediaSecurityType: string;
  type: string;
  indexDescription?: string | null;
  companyDescription?: string | null;
};

type MediaSecurityType = "Company" | "Fund" | "Index";

type MediaSecurityTypeLabelMap = Record<
  MediaSecurityType,
  { label: LabelIdKey; default: string }
>;

const MEDIA_SECURITY_TYPES_WITH_INFO_TAB = new Set([
  "Company",
  "Fund",
  "Index",
]);

const mediaSecurityTypeLabelMap: MediaSecurityTypeLabelMap = {
  Company: { label: "companyInfo", default: "Company Info" },
  Fund: { label: "fundInfo", default: "Fund Info" },
  Index: { label: "indexInfo", default: "Index Info" },
};

function trackTabClick(tabTitle: string) {
  trackClickEvent(tabTitle.toLocaleLowerCase().replace(/\s/g, "_"));
}

export default function QuotePagePanel(props: QuotePagePanelProps) {
  const { mediaSecurityType, type } = props;

  const shouldDisplayTab =
    MEDIA_SECURITY_TYPES_WITH_INFO_TAB.has(mediaSecurityType);
  const { isJapanese } = useLocale();
  const { pageData } = usePageData();

  const isMembersPage = useMemo(
    () => (pageData as QuotePageData)?.quotePageTab === "members",
    [pageData]
  );

  const translateMessage = useTranslateMessage();

  const infoTabLabel = useCallback(
    (securityType: MediaSecurityType) => {
      const { label: id, default: defaultMessage } =
        mediaSecurityTypeLabelMap[securityType];

      return translateMessage({
        id,
        defaultMessage,
      });
    },
    [translateMessage]
  );

  const summaryTabLabel = translateMessage({
    id: "summary",
    defaultMessage: "Summary",
  });

  const financialsTabLabel = translateMessage({
    id: "financials",
    defaultMessage: "Financials",
  });

  const membersTabLabel = translateMessage({
    id: "members",
    defaultMessage: "Members",
  });

  const tabList: React.ReactElement[] = useMemo(
    () =>
      [
        <Tab title={summaryTabLabel} key="summary">
          <SummaryContainer />
        </Tab>,
        !isJapanese && (
          <Tab title="Related News" key="related-news">
            <RelatedNewsContainer />
          </Tab>
        ),
        shouldDisplayTab && (
          <Tab
            title={infoTabLabel(mediaSecurityType as MediaSecurityType)}
            key="info"
          >
            <CompanyInfoContainer />
          </Tab>
        ),
        type === "EQT" && !isJapanese && (
          <Tab title={financialsTabLabel} key="financials">
            <FinancialsContainer />
          </Tab>
        ),
        isMembersPage && (
          <Tab title={membersTabLabel} isDefault key="members">
            <QuoteMembers />
          </Tab>
        ),
      ].filter(Boolean) as React.ReactElement[],
    [
      financialsTabLabel,
      infoTabLabel,
      isJapanese,
      isMembersPage,
      mediaSecurityType,
      shouldDisplayTab,
      summaryTabLabel,
      type,
    ]
  );

  return (
    <Tabs className={styles.tabbedView} onTabSwitch={trackTabClick}>
      {tabList}
    </Tabs>
  );
}
